import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Plus,
  FileText,
  Users,
  Building2,
  ClipboardList,
  CreditCard,
  Landmark,
  Zap,
  Plane,
  Briefcase,
  Receipt,
  Wallet,
  ArrowRight,
  Sparkles,
  Package,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  createClient, 
  createServiceOrder, 
  createInvoice,
  createGovtApplication,
  getClients,
  getServices,
  getGovtServices
} from '@/lib/api';

// Service Templates for common packages
const SERVICE_TEMPLATES = [
  {
    id: 'new-employee',
    name: 'New Employee Package',
    description: 'Work Permit + Visa + Emirates ID + Medical',
    icon: Briefcase,
    color: 'bg-blue-500',
    services: ['MOHRE_WORK_PERMIT', 'ICP_ENTRY_PERMIT', 'ICP_VISA_STAMPING', 'EID_NEW', 'TASH_MEDICAL']
  },
  {
    id: 'visa-renewal',
    name: 'Visa Renewal Package',
    description: 'Visa Renewal + Emirates ID Renewal',
    icon: Plane,
    color: 'bg-teal-500',
    services: ['ICP_VISA_RENEWAL', 'EID_RENEWAL']
  },
  {
    id: 'family-visa',
    name: 'Family Visa Package',
    description: 'Family Visa + Stamping + Emirates ID',
    icon: Users,
    color: 'bg-purple-500',
    services: ['ICP_FAMILY_VISA', 'ICP_VISA_STAMPING', 'EID_NEW']
  },
  {
    id: 'cancellation',
    name: 'Cancellation Package',
    description: 'Employment Cancel + Visa Cancel',
    icon: ClipboardList,
    color: 'bg-amber-500',
    services: ['MOHRE_CANCELLATION', 'ICP_VISA_CANCEL']
  }
];

// Quick action buttons configuration
const QUICK_ACTIONS = [
  { id: 'client', label: 'New Client', icon: Building2, color: 'bg-blue-500 hover:bg-blue-600' },
  { id: 'order', label: 'New Order', icon: ClipboardList, color: 'bg-teal-500 hover:bg-teal-600', navigate: '/service-orders/new' },
  { id: 'invoice', label: 'Quick Invoice', icon: FileText, color: 'bg-emerald-500 hover:bg-emerald-600' },
  { id: 'payment', label: 'Record Payment', icon: CreditCard, color: 'bg-violet-500 hover:bg-violet-600', navigate: '/payments?action=new' },
  { id: 'govt', label: 'Govt Application', icon: Landmark, color: 'bg-amber-500 hover:bg-amber-600', navigate: '/govt-services?action=new' },
  { id: 'template', label: 'Use Template', icon: Package, color: 'bg-rose-500 hover:bg-rose-600' },
];

export default function QuickActionsHub({ clients = [], onRefresh }) {
  const navigate = useNavigate();
  const [activeDialog, setActiveDialog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [govtServices, setGovtServices] = useState({});

  // Quick Client Form
  const [clientForm, setClientForm] = useState({
    client_type: 'Company',
    company_name: '',
    contact_person: '',
    phone: '',
    email: ''
  });

  // Quick Order Form
  const [orderForm, setOrderForm] = useState({
    client_id: '',
    service_id: '',
    applicant_name: '',
    priority: 'Normal'
  });

  // Template Form
  const [templateForm, setTemplateForm] = useState({
    client_id: '',
    applicant_name: '',
    template_id: ''
  });

  const loadServices = async () => {
    try {
      const [svcRes, govtRes] = await Promise.all([
        getServices({ limit: 100 }),
        getGovtServices()
      ]);
      setServices(svcRes.data.items || []);
      setGovtServices(govtRes.data || {});
    } catch (e) {
      console.error(e);
    }
  };

  const handleAction = async (actionId) => {
    // Find the action
    const action = QUICK_ACTIONS.find(a => a.id === actionId);
    
    // If action has direct navigation, go there
    if (action?.navigate) {
      navigate(action.navigate);
      return;
    }
    
    if (['template'].includes(actionId)) {
      await loadServices();
    }
    
    setActiveDialog(actionId);
  };

  const handleQuickClient = async () => {
    if (!clientForm.company_name && !clientForm.contact_person) {
      toast.error('Please enter company or contact name');
      return;
    }
    
    setLoading(true);
    try {
      const res = await createClient({
        ...clientForm,
        display_name: clientForm.company_name || clientForm.contact_person
      });
      toast.success(`Client "${res.data.display_name}" created!`);
      setActiveDialog(null);
      setClientForm({ client_type: 'Company', company_name: '', contact_person: '', phone: '', email: '' });
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error('Failed to create client');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickOrder = async () => {
    if (!orderForm.client_id || !orderForm.service_id) {
      toast.error('Please select client and service');
      return;
    }
    
    setLoading(true);
    try {
      const service = services.find(s => s.id === orderForm.service_id);
      const client = clients.find(c => c.id === orderForm.client_id);
      
      await createServiceOrder({
        client_id: orderForm.client_id,
        items: [{
          service_id: orderForm.service_id,
          service_name: service?.name || '',
          service_fee: service?.service_fee || 0,
          govt_fee: service?.govt_fee || 0,
          quantity: 1
        }],
        applicant_name: orderForm.applicant_name || client?.display_name || '',
        priority: orderForm.priority,
        notes: ''
      });
      toast.success('Service order created!');
      setActiveDialog(null);
      setOrderForm({ client_id: '', service_id: '', applicant_name: '', priority: 'Normal' });
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error('Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  const handleUseTemplate = async () => {
    if (!templateForm.client_id || !templateForm.template_id || !templateForm.applicant_name) {
      toast.error('Please fill all required fields');
      return;
    }
    
    const template = SERVICE_TEMPLATES.find(t => t.id === templateForm.template_id);
    if (!template) return;
    
    setLoading(true);
    try {
      const client = clients.find(c => c.id === templateForm.client_id);
      
      // Create government applications for each service in template
      for (const serviceCode of template.services) {
        // Find department and service info
        let department = '';
        let serviceName = '';
        
        for (const [dept, data] of Object.entries(govtServices)) {
          const svc = data.services?.find(s => s.code === serviceCode);
          if (svc) {
            department = dept;
            serviceName = svc.name;
            break;
          }
        }
        
        if (department) {
          await createGovtApplication({
            department,
            service_code: serviceCode,
            client_id: templateForm.client_id,
            applicant_name: templateForm.applicant_name,
            notes: `Created from template: ${template.name}`
          });
        }
      }
      
      toast.success(`${template.services.length} applications created from "${template.name}"!`);
      setActiveDialog(null);
      setTemplateForm({ client_id: '', applicant_name: '', template_id: '' });
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error('Failed to create from template');
    } finally {
      setLoading(false);
    }
  };

  // Auto-fill applicant name when client is selected
  const handleClientSelect = (clientId, formSetter) => {
    formSetter(prev => ({ ...prev, client_id: clientId }));
    const client = clients.find(c => c.id === clientId);
    if (client) {
      formSetter(prev => ({ 
        ...prev, 
        applicant_name: prev.applicant_name || client.display_name || client.contact_person || ''
      }));
    }
  };

  return (
    <>
      <Card className="border-0 shadow-lg bg-gradient-to-br from-slate-50 to-white" data-testid="quick-actions-hub">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Zap className="w-5 h-5 text-amber-500" />
            Quick Actions
            <Badge variant="secondary" className="ml-2 text-xs">⌘+K for more</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {QUICK_ACTIONS.map((action) => {
              const Icon = action.icon;
              return (
                <Button
                  key={action.id}
                  variant="ghost"
                  className={`h-auto py-4 flex-col gap-2 ${action.color} text-white transition-all hover:scale-105 hover:shadow-lg`}
                  onClick={() => handleAction(action.id)}
                  data-testid={`quick-${action.id}`}
                >
                  <Icon className="w-6 h-6" />
                  <span className="text-xs font-medium">{action.label}</span>
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Quick Client Dialog */}
      <Dialog open={activeDialog === 'client'} onOpenChange={() => setActiveDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-500" />
              Quick New Client
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label>Client Type</Label>
              <Select value={clientForm.client_type} onValueChange={(v) => setClientForm(p => ({ ...p, client_type: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Company">Company</SelectItem>
                  <SelectItem value="Individual">Individual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{clientForm.client_type === 'Company' ? 'Company Name *' : 'Full Name *'}</Label>
              <Input
                value={clientForm.company_name}
                onChange={(e) => setClientForm(p => ({ ...p, company_name: e.target.value }))}
                placeholder={clientForm.client_type === 'Company' ? 'ABC Trading LLC' : 'John Smith'}
                autoFocus
              />
            </div>
            {clientForm.client_type === 'Company' && (
              <div>
                <Label>Contact Person</Label>
                <Input
                  value={clientForm.contact_person}
                  onChange={(e) => setClientForm(p => ({ ...p, contact_person: e.target.value }))}
                  placeholder="Contact name"
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Phone</Label>
                <Input
                  value={clientForm.phone}
                  onChange={(e) => setClientForm(p => ({ ...p, phone: e.target.value }))}
                  placeholder="+971 50 123 4567"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  value={clientForm.email}
                  onChange={(e) => setClientForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="email@example.com"
                  type="email"
                />
              </div>
            </div>
            <Button className="w-full" onClick={handleQuickClient} disabled={loading}>
              <Plus className="w-4 h-4 mr-2" />
              {loading ? 'Creating...' : 'Create Client'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Order Dialog */}
      <Dialog open={activeDialog === 'order'} onOpenChange={() => setActiveDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-teal-500" />
              Quick Service Order
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label>Client *</Label>
              <Select 
                value={orderForm.client_id} 
                onValueChange={(v) => handleClientSelect(v, setOrderForm)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.display_name || c.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Service *</Label>
              <Select value={orderForm.service_id} onValueChange={(v) => setOrderForm(p => ({ ...p, service_id: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Applicant Name</Label>
              <Input
                value={orderForm.applicant_name}
                onChange={(e) => setOrderForm(p => ({ ...p, applicant_name: e.target.value }))}
                placeholder="Auto-filled from client"
              />
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={orderForm.priority} onValueChange={(v) => setOrderForm(p => ({ ...p, priority: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Normal">Normal</SelectItem>
                  <SelectItem value="Urgent">Urgent</SelectItem>
                  <SelectItem value="Express">Express</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button className="w-full" onClick={handleQuickOrder} disabled={loading}>
              <Plus className="w-4 h-4 mr-2" />
              {loading ? 'Creating...' : 'Create Order'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Invoice Dialog - Navigate to invoices page */}
      <Dialog open={activeDialog === 'invoice'} onOpenChange={() => setActiveDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-500" />
              Quick Invoice
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">
              Select a client to create a quick invoice from their pending service orders.
            </p>
            <Select onValueChange={(clientId) => {
              setActiveDialog(null);
              navigate(`/invoices?action=new&client_id=${clientId}`);
            }}>
              <SelectTrigger>
                <SelectValue placeholder="Select client" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.display_name || c.company_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" className="w-full" onClick={() => navigate('/invoices?action=new')}>
              <ArrowRight className="w-4 h-4 mr-2" />
              Go to Full Invoice Form
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Government Application Dialog */}
      <Dialog open={activeDialog === 'govt'} onOpenChange={() => setActiveDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Landmark className="w-5 h-5 text-amber-500" />
              Quick Govt Application
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">
              Go to Government Services to create a new application with full options.
            </p>
            <Button className="w-full" onClick={() => { setActiveDialog(null); navigate('/govt-services?tab=applications'); }}>
              <ArrowRight className="w-4 h-4 mr-2" />
              Open Government Services
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Service Template Dialog */}
      <Dialog open={activeDialog === 'template'} onOpenChange={() => setActiveDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-rose-500" />
              Use Service Template
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label>Select Template *</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                {SERVICE_TEMPLATES.map((template) => {
                  const Icon = template.icon;
                  const isSelected = templateForm.template_id === template.id;
                  return (
                    <button
                      key={template.id}
                      onClick={() => setTemplateForm(p => ({ ...p, template_id: template.id }))}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        isSelected 
                          ? 'border-teal-500 bg-teal-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <div className={`w-8 h-8 rounded-lg ${template.color} flex items-center justify-center`}>
                          <Icon className="w-4 h-4 text-white" />
                        </div>
                        {isSelected && <CheckCircle2 className="w-4 h-4 text-teal-500 ml-auto" />}
                      </div>
                      <p className="font-medium text-sm">{template.name}</p>
                      <p className="text-xs text-muted-foreground">{template.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label>Client *</Label>
              <Select 
                value={templateForm.client_id} 
                onValueChange={(v) => handleClientSelect(v, setTemplateForm)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.display_name || c.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Applicant Name *</Label>
              <Input
                value={templateForm.applicant_name}
                onChange={(e) => setTemplateForm(p => ({ ...p, applicant_name: e.target.value }))}
                placeholder="Full name as per passport"
              />
            </div>
            <Button className="w-full" onClick={handleUseTemplate} disabled={loading}>
              <Sparkles className="w-4 h-4 mr-2" />
              {loading ? 'Creating Applications...' : 'Create All Applications'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
